import React, { useState, useEffect } from 'react';
import { consumeApi } from '../../../api/consumeApi.js';
import { catalogApi } from '../../../api/catalogApi.js';
import { Modal } from '../../shared';

const EatItemModal = ({ initialItemId, onClose, onSubmit, useInternalModal = true }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [itemId, setItemId] = useState(initialItemId ?? '');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dateTime, setDateTime] = useState(() => {
    const d = new Date();
    // format for <input type="datetime-local">
    const pad = (n) => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  });
  const [servings, setServings] = useState('1');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await catalogApi.getItems();

        const root =
          resp?.data?.rows ??
          resp?.data?.items ??
          resp?.data ??
          resp ??
          [];

        const list = Array.isArray(root)
          ? root
          : Array.isArray(root.items)
          ? root.items
          : Array.isArray(root.rows)
          ? root.rows
          : Array.isArray(root?.rows?.items)
          ? root.rows.items
          : [];

        const mapped = list
          .map(i => ({
            id: i?.itemID ?? i?.id,
            name: i?.brand + ' ' + (i?.itemName ?? i?.name ?? `Item ${i?.itemID ?? i?.id ?? ''}`),
          }))
          .filter(m => m.id != null);

        if (!cancelled) {
          setItems(mapped);

          // Ensure a selection
          if (initialItemId && mapped.some(m => String(m.id) === String(initialItemId))) {
            setItemId(String(initialItemId));
          } else if (!initialItemId && mapped.length) {
            setItemId(String(mapped[0].id));
          } else if (!mapped.length) {
            // leave as-is (empty) when no items available
            setItemId('');
          }
        }
      } catch (e) {
        console.error('catalogApi.getItems error', e);
        if (!cancelled) setError('Failed to load catalog items');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [initialItemId]);

  const servingsNum = Number(servings);
  const isValid = itemId && dateTime && !Number.isNaN(servingsNum) && servingsNum > 0;

  // Filter items based on search query
  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get the selected item's name for display
  const selectedItem = items.find(it => String(it.id) === String(itemId));
  const displayValue = isDropdownOpen ? searchQuery : (selectedItem?.name || '');

  const handleSubmit = async () => {
    if (!isValid) return;

    const eatenOn = dateTime.replace('T', ' ');
    const payload = {
      itemID: Number(itemId),
      eatenOn: eatenOn, // local datetime string from input
      servingsEaten: servingsNum,
    };
    
    const response = await consumeApi.eatItem(payload);
    if (response.status === true) {
        console.log("Success");
    } else {
        console.error("Error creating the item:", response.msg);
    }

    onSubmit?.(payload);
    onClose?.();
  };

  const content = (
    <>
      {loading && <div style={{ opacity: 0.7, fontStyle: 'italic' }}>Loading…</div>}
      {error && <div style={{ color: '#b00020', marginBottom: 8 }}>{error}</div>}

      {!loading && !error && (
        <div style={{ display: 'grid', gap: 12, maxWidth: 520 }}>
          {/* Item selector - Searchable */}
          <label style={{ display: 'grid', gap: 6, position: 'relative' }}>
            <span style={{ fontWeight: 600 }}>Item</span>
            <input
              type="text"
              value={displayValue}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setIsDropdownOpen(true);
              }}
              onFocus={() => {
                setSearchQuery('');
                setIsDropdownOpen(true);
              }}
              onBlur={() => {
                // Delay to allow click on dropdown items
                setTimeout(() => setIsDropdownOpen(false), 200);
              }}
              placeholder="Search for an item..."
              style={{
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid #ddd',
              }}
            />
            {isDropdownOpen && filteredItems.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: 4,
                  maxHeight: 300,
                  overflowY: 'auto',
                  backgroundColor: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  zIndex: 1000,
                }}
              >
                {filteredItems.map(it => (
                  <div
                    key={it.id}
                    onClick={() => {
                      setItemId(String(it.id));
                      setSearchQuery('');
                      setIsDropdownOpen(false);
                    }}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      backgroundColor: String(it.id) === String(itemId) ? '#e3f2fd' : 'transparent',
                      borderBottom: '1px solid #eee',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = String(it.id) === String(itemId) ? '#e3f2fd' : 'transparent';
                    }}
                  >
                    {it.name}
                  </div>
                ))}
              </div>
            )}
            {items.length === 0 && (
              <small style={{ opacity: 0.7 }}>No items available.</small>
            )}
            {isDropdownOpen && filteredItems.length === 0 && searchQuery && (
              <small style={{ opacity: 0.7 }}>No items match your search.</small>
            )}
          </label>

          {/* Date/Time */}
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontWeight: 600 }}>Date &amp; Time</span>
            <input
              type="datetime-local"
              value={dateTime}
              onChange={(e) => setDateTime(e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid #ddd',
              }}
            />
          </label>

          {/* Servings */}
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontWeight: 600 }}>Servings</span>
            <input
              type="number"
              inputMode="decimal"
              step="any"
              min="0.0000001"
              value={servings}
              onChange={(e) => setServings(e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid #ddd',
              }}
            />
            <small style={{ opacity: 0.7 }}>
              Must be a number greater than 0. Decimals allowed.
            </small>
          </label>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: '1px solid #ccc',
                padding: '8px 12px',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              disabled={!isValid}
              onClick={handleSubmit}
              style={{
                background: isValid ? '#1E88E5' : '#90CAF9',
                color: '#fff',
                border: 'none',
                padding: '8px 14px',
                borderRadius: 8,
                cursor: isValid ? 'pointer' : 'not-allowed',
                fontWeight: 600,
              }}
            >
              Submit
            </button>
          </div>
        </div>
      )}
    </>
  );

  if (useInternalModal) {
    return (
      <Modal title="Eat Item" onClose={onClose}>
        {content}
      </Modal>
    );
  }

  // Embedded mode: return just the content (no wrapper Modal)
  return content;
};

export { EatItemModal };
